import { useMemo } from "react";
import type { ColumnDef } from "../../components/base-table-layout";
import { createEhrWriteBackColumn } from "../../ehr/create-ehr-column";
import { useEhrContext } from "../../../shared-logic/ehr-context/ehr-context";
import { BaseTableLayout } from "../../components/base-table-layout";
import { TableFilterPopover } from "../../components/table-filter-popover";
import { useTableFilters } from "../../hooks/use-table-filters";
import { createProcedureFilters } from "../../utils/procedure-filters";
import { TableRow } from "../../utils/tables-metadata";
import { ProcedureRowData } from "./procedure-rows";

export type ProcedureTableProps = {
  rows: TableRow[];
  onRowClick: (row: TableRow) => void;
  filterQuery: string;
  onPdfView?: (docId: string) => void;
  displayName?: string;
  isRowViewed?: (row: TableRow) => boolean;
};

export function ProcedureTable({
  rows,
  onRowClick,
  filterQuery,
  displayName = "Procedures",
  isRowViewed,
}: ProcedureTableProps) {
  const ehrContext = useEhrContext();

  const columns = useMemo(() => {
    const cols: ColumnDef<ProcedureRowData>[] = [
      { key: "procedure", header: "Procedure" },
      { key: "type", header: "Type", isHiddenOnSmallScreen: true },
      { key: "bodyRegion", header: "Body Region", isHiddenOnSmallScreen: true },
      {
        key: "datePerformed",
        header: "Date Performed",
        width: "120px",
        sort: "desc",
      },
      { key: "status", header: "Status", isHiddenOnSmallScreen: true },
    ];

    const ehrColumn = createEhrWriteBackColumn<ProcedureRowData>(ehrContext, "procedure");
    if (ehrColumn) cols.push(ehrColumn);

    return cols;
  }, [ehrContext]);

  const filterOptions = useMemo(() => {
    const procedureRows = rows as ProcedureRowData[];
    const filters = createProcedureFilters(procedureRows);

    return filters.map(option => ({
      label: option.label,
      groupLabel: option.groupLabel,
      isChecked: true, // All filters enabled by default
      filterFn: (rowData: ProcedureRowData) => {
        const [type, value] = option.value.split(":");
        switch (type) {
          case "type":
            if (value === "undefined") {
              return !rowData.type || rowData.type === "-";
            }
            return rowData.type === value;
          case "bodyRegion":
            if (value === "undefined") {
              return !rowData.bodyRegion || rowData.bodyRegion === "-";
            }
            return rowData.bodyRegion === value;
          default:
            return true;
        }
      },
    }));
  }, [rows]);

  const { activeFilters, setActiveFilters, filteredRows } = useTableFilters(rows, filterOptions);

  function handleRowClick(row: TableRow) {
    onRowClick(row);
  }

  return (
    <BaseTableLayout
      title={displayName}
      rows={filteredRows as unknown as ProcedureRowData[]}
      columns={columns}
      filterQuery={filterQuery}
      onRowClick={handleRowClick}
      isRowViewed={isRowViewed}
      headerActionsSlot={
        filterOptions.length > 0 ? (
          <TableFilterPopover
            filters={filterOptions}
            activeFilters={activeFilters}
            setActiveFilters={newFilters => setActiveFilters(newFilters)}
          />
        ) : undefined
      }
    />
  );
}
